import {
  BarChart3,
  ClipboardList,
  Flag,
  GraduationCap,
  ListChecks,
  Upload,
  LogOut,
  Mail,
  Menu,
  Network,
  RefreshCw,
  ShieldCheck,
  UserCog,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, Navigate, useNavigate } from 'react-router-dom'
import { ApiError, getAdminRole, getAdminToken, setAdminRole, setAdminToken } from '../../api/client'
import { adminApi } from '../../api/endpoints'
import { Dot } from '../../components/Logo'
import type { AdminRole } from '../../types/api'

/**
 * Which console screens each admin role (V42) sees. The server enforces the same split in
 * SecurityConfig; this only decides what to draw, so a screen hidden here is also refused there.
 */
export type AdminPage =
  | 'verifications'
  | 'providers'
  | 'sync'
  | 'bulkUpload'
  | 'emailTemplates'
  | 'aggregators'
  | 'analytics'
  | 'trust'
  | 'team'

const ALL: AdminRole[] = ['SUPER_ADMIN', 'ADMIN', 'VERIFIER', 'UPLOADER']
const STAFF: AdminRole[] = ['SUPER_ADMIN', 'ADMIN']

/** In nav order. A role's home is the first entry it may see. */
export const ADMIN_PAGES: { page: AdminPage; path: string; label: string; icon: ReactNode; roles: AdminRole[] }[] = [
  { page: 'analytics', path: '/admin/analytics', label: 'Dashboard', icon: <BarChart3 size={17} />, roles: STAFF },
  { page: 'providers', path: '/admin/providers', label: 'Provider report', icon: <ListChecks size={17} />, roles: STAFF },
  { page: 'sync', path: '/admin/sync', label: 'Sync', icon: <RefreshCw size={17} />, roles: STAFF },
  { page: 'verifications', path: '/admin', label: 'Verifications', icon: <ShieldCheck size={17} />, roles: ['SUPER_ADMIN', 'ADMIN', 'VERIFIER'] },
  { page: 'bulkUpload', path: '/admin/bulk-upload/seekers', label: 'Bulk upload', icon: <Upload size={17} />, roles: ['SUPER_ADMIN', 'ADMIN', 'UPLOADER'] },
  { page: 'emailTemplates', path: '/admin/email-templates', label: 'Email templates', icon: <Mail size={17} />, roles: ['SUPER_ADMIN'] },
  { page: 'aggregators', path: '/admin/aggregators', label: 'Aggregators', icon: <Network size={17} />, roles: ['SUPER_ADMIN'] },
  { page: 'trust', path: '/admin/reports', label: 'Reports & blocks', icon: <Flag size={17} />, roles: ['SUPER_ADMIN', 'ADMIN', 'VERIFIER'] },
  { page: 'team', path: '/admin/team', label: 'Team', icon: <UserCog size={17} />, roles: ['SUPER_ADMIN'] },
]

export const ROLE_LABEL: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super admin',
  ADMIN: 'Admin',
  VERIFIER: 'Reviewer',
  UPLOADER: 'Creator',
}

const isRole = (r: string | null): r is AdminRole => !!r && (ALL as string[]).includes(r)

export function canSee(role: AdminRole, page: AdminPage): boolean {
  return ADMIN_PAGES.find((p) => p.page === page)!.roles.includes(role)
}

/** Where a role lands after sign-in, and where it is sent from a screen it may not open. */
export function homeFor(role: AdminRole): string {
  return ADMIN_PAGES.find((p) => p.roles.includes(role))!.path
}

/**
 * The signed-in admin's role: the cached one at once, then refreshed from /admin/me - so a role a
 * super admin changed shows up on the next screen, and a disabled account is signed out.
 */
export function useAdminRole(): AdminRole | null {
  const navigate = useNavigate()
  const cached = getAdminRole()
  const [role, setRole] = useState<AdminRole | null>(isRole(cached) ? cached : null)

  useEffect(() => {
    if (!getAdminToken()) return
    let live = true
    adminApi
      .me()
      .then((me) => {
        setAdminRole(me.role)
        if (live) setRole(me.role)
      })
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setAdminToken(null)
          navigate('/admin/login', { replace: true })
        }
      })
    return () => {
      live = false
    }
  }, [navigate])

  return role
}

/** Wraps each admin route: signed out goes to the login, a screen the role may not open goes home. */
export function AdminGate({ page, children }: { page: AdminPage; children: ReactNode }) {
  const role = useAdminRole()
  if (!getAdminToken()) return <Navigate to="/admin/login" replace />
  if (!role) return <div className="min-h-screen bg-ink-100" />
  if (!canSee(role, page)) return <Navigate to={homeFor(role)} replace />
  return <>{children}</>
}

/** The three screens under the one Bulk upload nav item, in tab order. */
const BULK_UPLOAD_TABS: { path: string; label: string; icon: ReactNode }[] = [
  { path: '/admin/bulk-upload/seekers', label: 'Seekers upload', icon: <Users size={14} /> },
  { path: '/admin/bulk-upload/tutors', label: 'Tutors upload', icon: <GraduationCap size={14} /> },
  { path: '/admin/bulk-upload/report', label: 'Report status', icon: <ClipboardList size={14} /> },
]

/** The tab bar at the top of each Bulk upload screen. */
export function BulkUploadTabs() {
  return (
    <nav aria-label="Bulk upload" className="flex flex-wrap gap-1 border-b border-ink-200">
      {BULK_UPLOAD_TABS.map((t) => (
        <NavLink
          key={t.path}
          to={t.path}
          className={({ isActive }) =>
            `-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold ${
              isActive ? 'border-sun-500 text-ink-900' : 'border-transparent text-ink-500 hover:text-ink-900'
            }`
          }
        >
          {t.icon} {t.label}
        </NavLink>
      ))}
    </nav>
  )
}

/**
 * The console chrome every admin screen shares: a left rail with only the screens the role may
 * open (the current one marked rather than linked), and a top bar with the screen's name, its own
 * actions ({@code children}) and - on phones - the button that opens the rail as a drawer.
 *
 * <p>The rail is fixed, so each screen's root adds {@code lg:pl-64} to clear it.
 */
export function AdminHeader({
  current,
  onSignOut,
  children,
  width = 'max-w-7xl',
}: {
  current: AdminPage
  onSignOut: () => void
  children?: ReactNode
  width?: string
}) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const role = isRole(getAdminRole()) ? (getAdminRole() as AdminRole) : null
  const pages = role ? ADMIN_PAGES.filter((p) => p.roles.includes(role)) : []
  const title = ADMIN_PAGES.find((p) => p.page === current)?.label

  const rail = (
    <div className="flex h-full flex-col bg-deep-900 text-cream-300">
      <div className="flex items-center gap-3 px-5 pb-6 pt-6">
        <span className="relative grid h-10 w-10 place-items-center rounded-xl bg-white/5 ring-1 ring-white/10">
          <span className="absolute inset-0 rounded-xl bg-sun-400/20 blur-md" aria-hidden />
          <Dot size={20} className="relative" />
        </span>
        <div className="leading-tight">
          <div className="font-extrabold tracking-tight text-white">YellowDots</div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sun-400">Ops console</div>
        </div>
      </div>

      <div className="px-5 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-cream-400/60">Menu</div>
      <nav aria-label="Admin" className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 pb-4">
        {pages.map((p) => {
          const active = p.page === current
          return (
            <button
              key={p.page}
              onClick={() => {
                setMenuOpen(false)
                if (!active) navigate(p.path)
              }}
              aria-current={active ? 'page' : undefined}
              className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition ${
                active
                  ? 'bg-gradient-to-r from-sun-400 to-sun-300 text-ink-900 shadow-lg shadow-sun-500/20'
                  : 'text-cream-300/80 hover:bg-white/5 hover:text-white'
              }`}
            >
              <span
                className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition ${
                  active ? 'bg-ink-900/10' : 'bg-white/5 group-hover:bg-white/10'
                }`}
              >
                {p.icon}
              </span>
              {p.label}
            </button>
          )
        })}
      </nav>

      <div className="m-3 rounded-2xl bg-white/5 p-3 ring-1 ring-white/10">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-400">
            <ShieldCheck size={17} />
          </span>
          <div className="min-w-0 leading-tight">
            <div className="text-[11px] uppercase tracking-wider text-cream-400/70">Signed in as</div>
            <div className="truncate text-sm font-bold text-white">{role ? ROLE_LABEL[role] : 'Admin'}</div>
          </div>
        </div>
        <button
          onClick={onSignOut}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 py-2 text-sm font-semibold text-cream-300 transition hover:border-red-400/40 hover:bg-red-500/10 hover:text-red-300"
        >
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </div>
  )

  return (
    <>
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 shadow-xl lg:block">{rail}</aside>

      {menuOpen && (
        <div className="fixed inset-0 z-[1000] lg:hidden">
          <button
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 h-full w-full bg-ink-900/50 backdrop-blur-sm"
          />
          <aside className="absolute inset-y-0 left-0 w-64 max-w-[85vw] shadow-2xl">
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="absolute right-2 top-3 z-10 grid h-9 w-9 place-items-center rounded-full text-cream-300 hover:bg-white/10"
            >
              <X size={18} />
            </button>
            {rail}
          </aside>
        </div>
      )}

      <header className="sticky top-0 z-20 border-b border-ink-200/70 bg-white/85 backdrop-blur">
        <div className={`mx-auto flex ${width} flex-wrap items-center justify-between gap-3 px-4 py-3`}>
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink-200 text-ink-600 transition hover:bg-ink-100 lg:hidden"
            >
              <Menu size={17} />
            </button>
            <div className="min-w-0 leading-tight">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-500">YellowDots Ops</div>
              <div className="truncate text-base font-bold text-ink-900">{title}</div>
            </div>
          </div>
          {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
        </div>
      </header>
    </>
  )
}
